import { Tables } from "@/types/supabase-types";
import { REALTIME_POSTGRES_CHANGES_LISTEN_EVENT } from "@supabase/supabase-js";
import { supabase } from "./supabase";
import { getLongRunningGASRequestStatus, startLongRunningGASRequest } from "./supabase/edge-functions/gas";
import { SupabaseRealtimePostgresEventStream } from "./supabase/supabase-realtime-postgres-event-stream";

export type ReportProgressCallback = (progress: string) => void;

export async function syncAllEntities(reportProgress: ReportProgressCallback): Promise<void> {
  await executeLongRunningRequest(reportProgress, "syncAllEntities");
}

export async function uploadBOMSyncData(reportProgress: ReportProgressCallback, csvText: string): Promise<void> {
  await executeLongRunningRequest(reportProgress, "uploadBOMSyncData", {}, csvText);
}

export async function uploadVendorItemCatalogSyncData(
  reportProgress: ReportProgressCallback,
  csvText: string,
): Promise<void> {
  await executeLongRunningRequest(reportProgress, "uploadVendorItemCatalogSyncData", {}, csvText);
}

async function executeLongRunningRequest<TResponse = void>(
  reportProgress: ReportProgressCallback,
  gasApiMethod: string,
  queryParams?: URLSearchParams | string | Record<string, string>,
  body?: BodyInit,
): Promise<TResponse> {
  // Call an edge function to start execution of the long-running request
  console.info(`Starting execution of long-running GAS API method: ${gasApiMethod}`);
  const requestId = await startLongRunningGASRequest(gasApiMethod, queryParams, body);
  console.info(`Long-running GAS API method '${gasApiMethod}' started with request ID: ${requestId}`);

  // Get a stream of status updates for the long-running request
  // NOTE: Use a unique channel name based on the request ID so that we don't collide with other request channels
  const eventStream = new SupabaseRealtimePostgresEventStream<
    REALTIME_POSTGRES_CHANGES_LISTEN_EVENT.UPDATE,
    Tables<"LongRunningGasRequest">
  >(supabase, `LongRunningGasRequest-${requestId}`, {
    event: REALTIME_POSTGRES_CHANGES_LISTEN_EVENT.UPDATE,
    schema: "public",
    table: "LongRunningGasRequest",
    filter: `id=eq.${requestId}`,
  });

  // TODO: Remove this try-catch block once we can switch to the `await using` syntax. This will be available in the
  // Firefox 141 release, on July 22, 2025 (see: https://developer.mozilla.org/en-US/docs/Mozilla/Firefox/Releases/141)
  try {
    // Get the initial status of the long-running request
    const { data: initialStatus } = await supabase
      .from("LongRunningGasRequest")
      .select("*")
      .eq("id", requestId)
      .single()
      .throwOnError();
    console.info(
      `Fetched initial status of long-running GAS API method '${gasApiMethod}' (${requestId}):`,
      initialStatus,
    );

    // Prepend the initial status to the event stream
    const eventStreamWithInitialStatus = (async function* () {
      yield initialStatus;
      for await (const payload of eventStream) yield payload.new;
    })();

    // Send a progress update for each event, and break when the request is complete
    for await (const payload of eventStreamWithInitialStatus) {
      if (payload.progress) reportProgress(payload.progress);
      if (payload.status === "completed") break;
    }
  } finally {
    await eventStream[Symbol.asyncDispose]();
  }

  // Call an edge function to get the result of the long-running request
  // NOTE: This is necessary rather than just getting the result from the realtime subscription because the API call
  // to fetch the result also makes sure to delete the request information in the database
  const result = await getLongRunningGASRequestStatus<TResponse>(requestId);

  // Throw an error if the request failed
  if (result.status !== "success") {
    const errorDetails = result.data;
    throw new Error(`'${gasApiMethod}' request failed: ${errorDetails.message} -> ${errorDetails.cause}`);
  }

  // Return the result of the long-running request
  return result.data;
}
